/**
 * Turning platform values into text.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * DELIBERATELY NOT THE PRODUCT CONSOLE'S format.ts
 * ══════════════════════════════════════════════════════════════════════════
 *
 * That file is a money parser and nothing else, because rendering there goes
 * through `finance/Money.tsx`. Copying it here would have brought a parser for
 * typed rupee amounts into a console that never shows a rupee: this one sees
 * row counts, timestamps and email addresses. Reading a seller's figures needs
 * a tenant token, which no admin holds.
 *
 * So this is small and specific, and it will stay that way unless the admin
 * console grows a reason to render money — at which point the answer is
 * `Money.tsx`, not a second formatter here.
 */

/**
 * A count, grouped the Indian way.
 *
 * `en-IN` gives 1,18,760 rather than 118,760. Correct for the reader: this is
 * an Indian product and the people looking at these numbers group them in
 * lakhs. The locale is pinned rather than left to the browser, so a screenshot
 * from a laptop set to en-US shows the same thing as one from Bangalore.
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-IN').format(value);
}

/**
 * A timestamp, as a date and a time in IST.
 *
 * ── WHY THE ZONE IS PINNED ────────────────────────────────────────────────
 *
 * The platform stores `timestamptz` and serialises UTC. Left to the browser,
 * the same audit row reads as two different times to two people, and "who
 * purged this and when" is a question that has to have one answer. Asia/Kolkata
 * is where this is operated from.
 *
 * The time is shown, not just the date. On an audit trail the ordering within
 * a day is the whole point — a purge at 09:14 and a sign-in at 09:13 tell a
 * story that "29 Aug 2026" twice does not.
 */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
}

/** A date without the time, for things where the hour is noise. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
}
